import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { type CountryCode } from 'libphonenumber-js'
import { formatPhoneNumberNationalWithCode } from 'shared/lib'
import { CountryFlag } from 'shared/ui/CountryFlag/CountryFlag'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type IRcsComplianceData } from 'entities/Rcs'
import { RcsInfoFormRow } from 'pages/settings/pages/rcsV2/ui'
import {
  RCS_SUPPORTED_COUNTRIES,
  US_COUNTRY_ISO_CODE,
} from 'pages/settings/pages/rcsV2/lib/constants'
import { RcsMediaValue, RcsViewLink, RcsViewSection, RcsViewValue } from './RcsViewParts'

import styles from './styles.module.scss'

const COUNTRY_FLAG_MAP: Record<string, CountryCode> = {
  us: 'US',
  ca: 'CA',
  uk: 'GB',
}

const formatEin = (ein: string) => (ein.length === 9 ? `${ein.slice(0, 2)}-${ein.slice(2)}` : ein)

const formatTrafficNumber = (value: number) => value.toLocaleString('en-US')

const getCountryLabel = (code: string) =>
  RCS_SUPPORTED_COUNTRIES.find((country) => country.isoCode === code)?.label ?? code

type IComplianceTabProps = {
  data: IRcsComplianceData | null
}

export const ComplianceTab = observer(({ data }: IComplianceTabProps) => {
  const { complianceStore } = useEntitiesContext()
  const stateCode = data?.['business-info']?.state

  useEffect(() => {
    if (stateCode) complianceStore.loadBusinessProfileStates()
  }, [complianceStore, stateCode])

  if (!data) return null

  const businessInfo = data['business-info']
  const useCase = data.use_case
  const messageInfo = data.message_info

  const stateName = stateCode
    ? complianceStore.getStates(US_COUNTRY_ISO_CODE).find((item) => item.id === stateCode)?.label ??
      stateCode
    : ''

  return (
    <>
      {!!data.country?.length && (
        <RcsViewSection title='Countries'>
          <RcsInfoFormRow label='Registered in'>
            <div className={styles.countries}>
              {data.country.map((item) => {
                const flagCode = COUNTRY_FLAG_MAP[item.key]

                return (
                  <div key={item.key} className={styles.country}>
                    {flagCode && <CountryFlag country={flagCode} width={16} />}
                    <RcsViewValue>{item.label}</RcsViewValue>
                  </div>
                )
              })}
            </div>
          </RcsInfoFormRow>
        </RcsViewSection>
      )}

      {businessInfo && (
        <RcsViewSection title='Business info'>
          {businessInfo.ein && (
            <RcsInfoFormRow label='EIN'>
              <RcsViewValue>{formatEin(businessInfo.ein)}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.business_name && (
            <RcsInfoFormRow label='Legal business name'>
              <RcsViewValue>{businessInfo.business_name}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.address_country && (
            <RcsInfoFormRow label='Country'>
              <RcsViewValue>{getCountryLabel(businessInfo.address_country)}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.street_address_1 && (
            <RcsInfoFormRow label='Business address'>
              <RcsViewValue>{businessInfo.street_address_1}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.street_address_2 && (
            <RcsInfoFormRow label='Business address 2'>
              <RcsViewValue>{businessInfo.street_address_2}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.city && (
            <RcsInfoFormRow label='City'>
              <RcsViewValue>{businessInfo.city}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.state && (
            <RcsInfoFormRow label='State'>
              <RcsViewValue>{stateName}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.postal_code && (
            <RcsInfoFormRow label='ZIP code'>
              <RcsViewValue>{businessInfo.postal_code}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {data.business_phone && (
            <RcsInfoFormRow label='Business contact phone number'>
              <RcsViewValue>{formatPhoneNumberNationalWithCode(data.business_phone)}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.website_url && (
            <RcsInfoFormRow label='Business website URL'>
              <RcsViewLink href={businessInfo.website_url} />
            </RcsInfoFormRow>
          )}
          {data.company_type && (
            <RcsInfoFormRow label='Company type'>
              <RcsViewValue>{data.company_type.label}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {data.business_industry && (
            <RcsInfoFormRow label='Business industry'>
              <RcsViewValue>{data.business_industry.label}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.stock_exchange && (
            <RcsInfoFormRow label='Stock exchange'>
              <RcsViewValue>{businessInfo.stock_exchange}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {businessInfo.stock_symbol && (
            <RcsInfoFormRow label='Company Stock Ticker Symbol'>
              <RcsViewValue>{businessInfo.stock_symbol}</RcsViewValue>
            </RcsInfoFormRow>
          )}
        </RcsViewSection>
      )}

      {data.contact_info && (
        <RcsViewSection title='Contact info'>
          <RcsInfoFormRow label='First name'>
            <RcsViewValue>{data.contact_info.first_name}</RcsViewValue>
          </RcsInfoFormRow>
          <RcsInfoFormRow label='Last name'>
            <RcsViewValue>{data.contact_info.last_name}</RcsViewValue>
          </RcsInfoFormRow>
          <RcsInfoFormRow label='Business title'>
            <RcsViewValue>{data.contact_info.business_title}</RcsViewValue>
          </RcsInfoFormRow>
          <RcsInfoFormRow label='Email'>
            <RcsViewValue>{data.contact_info.business_email}</RcsViewValue>
          </RcsInfoFormRow>
          {data.contact_info.business_website_URL && (
            <RcsInfoFormRow label='Business website URL'>
              <RcsViewLink href={data.contact_info.business_website_URL} />
            </RcsInfoFormRow>
          )}
        </RcsViewSection>
      )}

      {useCase && (
        <RcsViewSection title='Use case'>
          {useCase.use_case && (
            <RcsInfoFormRow label='Type'>
              <RcsViewValue>{useCase.use_case.label}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          <RcsInfoFormRow
            label='Description'
            variant='paragraph'
            className={styles.paragraphSpacing}
          >
            <RcsViewValue>{useCase.use_case_description}</RcsViewValue>
          </RcsInfoFormRow>
          {useCase.use_case_video_url && (
            <RcsInfoFormRow
              label='Video'
              variant='media'
              className={styles.mediaSpacing}
              copyValue={useCase.use_case_video_url}
            >
              <RcsMediaValue url={useCase.use_case_video_url} isVideo />
            </RcsInfoFormRow>
          )}
          {typeof useCase.monthly_trafic === 'number' && (
            <RcsInfoFormRow label='Monthly organic website traffic'>
              <RcsViewValue>{formatTrafficNumber(useCase.monthly_trafic)}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {typeof useCase.have_shortcode_trafic === 'boolean' && (
            <RcsInfoFormRow label='Does this use case have existing short code traffic?'>
              <RcsViewValue>{useCase.have_shortcode_trafic ? 'Yes' : 'No'}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {useCase.shortcode && (
            <RcsInfoFormRow label='Provide the existing short code associated with this use case'>
              <RcsViewValue>{useCase.shortcode}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {typeof useCase.shortcode_trafic === 'number' && (
            <RcsInfoFormRow label='Estimated monthly traffic volume for the existing short code number'>
              <RcsViewValue>{formatTrafficNumber(useCase.shortcode_trafic)}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {typeof useCase.traffic_volume === 'number' && (
            <RcsInfoFormRow label='Expected monthly RCS sender traffic volume'>
              <RcsViewValue>{formatTrafficNumber(useCase.traffic_volume)}</RcsViewValue>
            </RcsInfoFormRow>
          )}
        </RcsViewSection>
      )}

      {messageInfo && (
        <RcsViewSection title='Opt-in & message info'>
          <RcsInfoFormRow label='Message flow and opt-in description'>
            <RcsViewValue>{messageInfo.opt_in_description}</RcsViewValue>
          </RcsInfoFormRow>
          {messageInfo.opt_in_image_url && (
            <RcsInfoFormRow
              label='Opt-in policy image'
              variant='media'
              className={styles.mediaSpacing}
              copyValue={messageInfo.opt_in_image_url}
            >
              <RcsMediaValue url={messageInfo.opt_in_image_url} />
            </RcsInfoFormRow>
          )}
          {messageInfo.opt_out_description && (
            <RcsInfoFormRow label='Opt-out description'>
              <RcsViewValue>{messageInfo.opt_out_description}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {messageInfo.sample_message && (
            <RcsInfoFormRow label='Sample help message'>
              <RcsViewValue>{messageInfo.sample_message}</RcsViewValue>
            </RcsInfoFormRow>
          )}
          {messageInfo.sample_opt_out_message && (
            <RcsInfoFormRow label='Sample opt-out message'>
              <RcsViewValue>{messageInfo.sample_opt_out_message}</RcsViewValue>
            </RcsInfoFormRow>
          )}
        </RcsViewSection>
      )}
    </>
  )
})
